import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRippleModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';

import { fuseAnimations } from '../../../../@fuse/animations';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import { PhotoLibraryService } from '../../api/photo-library.service';
import { GT2PageAbstract } from '../../content/abstract/GT2PageAbstract';
import { ModulesService } from '../../services/modules.service';
import { PhotoLibraryPanelService } from '../../services/photo-library-panel.service';
import { locale as english } from './i18n/en-CA';
import { locale as french } from './i18n/fr-CA';

interface LibraryPhoto {
  key: string;
  thumbUrl: string;
  applyUrl: string;
  label: string;
}

type LibraryTab = 'mine' | 'gt';

/**
 * Side panel « Sélectionner une image dans la bibliothèque » — box Photo
 * Produit/Matériel. Deux onglets (Ma bibliothèque / Bibliothèque GT), chacun
 * masqué si son module est absent. Une seule sélection ; « Ajouter » copie
 * l'image dans le produit/matériel via upload-by-url et retourne
 * { uuid, shop_image_url } au caller.
 */
@Component({
  selector: 'app-photo-library-panel',
  templateUrl: './photo-library-panel.component.html',
  styleUrls: ['./photo-library-panel.component.scss'],
  animations: fuseAnimations,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRippleModule,
    MatTooltipModule,
  ],
})
export class PhotoLibraryPanelComponent extends GT2PageAbstract implements OnInit {
  modelType = '';
  modelUuid = '';
  /** Catalog filter passed to the API ('product' | 'material'). */
  libType = '';

  activeTab: LibraryTab = 'mine';
  filterValue = '';

  mineItems: LibraryPhoto[] = [];
  gtItems: LibraryPhoto[] = [];
  private mineLoaded = false;
  private gtLoaded = false;

  isLoading = false;
  isSaving = false;
  selected: LibraryPhoto | null = null;

  constructor(
    private panelService: PhotoLibraryPanelService,
    private photoLibraryService: PhotoLibraryService,
    private translationLoader: FuseTranslationLoaderService,
    public translate: TranslateService,
    public modulesService: ModulesService,
    private dialog: MatDialog,
    public router: Router,
    private logger: NGXLogger,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);
    const data = this.panelService.getData();
    if (data) {
      this.modelType = data.modelType;
      this.modelUuid = data.modelUuid;
      this.libType = (data.modelType || '').toLowerCase();
    }
  }

  ngOnInit(): void {
    this.activeTab = this.modulesService.hasMyPhotoLib() ? 'mine' : 'gt';
    this.loadActiveTab();
  }

  get items(): LibraryPhoto[] {
    return this.activeTab === 'mine' ? this.mineItems : this.gtItems;
  }

  get filteredItems(): LibraryPhoto[] {
    const term = this.filterValue.trim().toLowerCase();
    if (term === '') {
      return this.items;
    }
    return this.items.filter((p) => (p.label || '').toLowerCase().includes(term));
  }

  public selectTab(tab: LibraryTab): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    this.selected = null;
    this.loadActiveTab();
  }

  public clearSearch(): void {
    this.filterValue = '';
  }

  public onSelect(photo: LibraryPhoto): void {
    this.selected = photo;
  }

  public isSelected(photo: LibraryPhoto): boolean {
    return this.selected !== null && this.selected.key === photo.key;
  }

  private loadActiveTab(): void {
    if (this.activeTab === 'mine') {
      if (!this.mineLoaded) {
        this.loadMine();
      }
    } else if (!this.gtLoaded) {
      this.loadGt();
    }
  }

  private loadMine(): void {
    this.isLoading = true;
    this.photoLibraryService.getMine(this.libType, '').subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.mineLoaded = true;
        this.mineItems = (response?.data || []).map((item: any) => ({
          key: item.uuid + '|' + item.url_large,
          thumbUrl: item.url || item.url_large,
          applyUrl: item.url_large || item.url,
          label: item.label || '',
        }));
      },
      error: (error: any) => {
        this.isLoading = false;
        this.logger.error(
          'PhotoLibraryPanelComponent.loadMine() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.handleAPIError(error, this.dialog, null, null, this.router);
      },
    });
  }

  private loadGt(): void {
    this.isLoading = true;
    this.photoLibraryService.getGt(this.libType, '').subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.gtLoaded = true;
        this.gtItems = (response?.data || []).map((item: any) => ({
          key: item.uuid,
          thumbUrl: item.thumb_url || item.url,
          applyUrl: item.url || item.thumb_url,
          label: item.label || '',
        }));
      },
      error: (error: any) => {
        this.isLoading = false;
        this.logger.error(
          'PhotoLibraryPanelComponent.loadGt() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.handleAPIError(error, this.dialog, null, null, this.router);
      },
    });
  }

  public onAdd(): void {
    if (this.selected === null || this.isSaving) {
      return;
    }
    this.isSaving = true;
    this.photoLibraryService
      .applyFromUrl(this.modelType, this.modelUuid, this.selected.applyUrl)
      .subscribe({
        next: (response: any) => {
          this.isSaving = false;
          this.panelService.emitResult(response?.data ?? response);
        },
        error: (error: any) => {
          this.isSaving = false;
          this.logger.error(
            'PhotoLibraryPanelComponent.onAdd() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  public close(): void {
    this.panelService.emitResult(false);
  }
}
